import { colors } from "../design-tokens";
import { isPinnable, listingStatusWord } from "./addressPin";
import type { CandidateComp } from "./types";

/**
 * THE MAP'S MODEL (1G) — which comps get which pin number, and which cannot be placed. Pure, so the
 * pin <-> row contract is testable without a map: the number on a pin is the sale's position in
 * the grid's own display order (`ConfirmGrid`'s `compOrder`, the order the cards are drawn in),
 * plus one. It does not renumber when a pin cannot be placed — "pin 4" is always the fourth card.
 *
 * COORDINATES ARE FOR THIS SCREEN ONLY. A row that states a latitude/longitude (an MLS or
 * public-record candidate) is placed from it; every other row is placed by geocoding its address in
 * the browser, behind the same pin-or-don't gate the review map uses (`addressPin.isPinnable`). The
 * deliverable's map is built server-side from ADDRESSES (`amb_build._map_markers`) and a test pins
 * that no MLS coordinate reaches a finished brief.
 */
export interface MapPoint {
  /** Index into the `comps` array — the identity the grid and the map share. A competing listing
   * carries `ACTIVE_INDEX_BASE + its position in the listings`, so the two never collide. */
  index: number;
  /** "comp" = a sale in the grid; "active" = a competing listing (Active or Pending). */
  kind: "comp" | "active";
  /** 1-based position in the grid's display order. 0 for a competing listing, which has no number. */
  number: number;
  /** What the pin says: the comp's number, or "A" (Active) / "P" (Pending). */
  glyph: string;
  /** What the pin is called in words: "Comp 3", "Active" or "Pending". */
  caption: string;
  /** Where the point came from ("MLS coordinates", "Census geocode", ...). Null while the browser
   * still has to geocode it; the map fills this in once it has a point. */
  source: string | null;
  name: string;
  selected: boolean;
  lat: number | null;
  lon: number | null;
  /** What to geocode when there is no coordinate. Null when there is neither (unplaceable). */
  query: string | null;
}

const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);

/** The sentence a row with no location prints. Said the same way on both maps. Never a guessed pin. */
export const NOT_MAPPED_MESSAGE = "Not mapped: no location on record";
/** Competing listings are indexed from here so their identity never meets a comp's array index. */
export const ACTIVE_INDEX_BASE = 100000;
/** Where a browser geocode came from, as the source line prints it. */
export const BROWSER_GEOCODE_SOURCE = "Google geocode";

/** A coordinate off the wire: a finite number, or a non-blank numeric string. Anything else is missing. */
function coord(v: unknown): number | null {
  if (finite(v)) return v;
  if (typeof v !== "string" || !v.trim()) return null;
  const n = Number(v.trim());
  return Number.isFinite(n) ? n : null;
}

/** A coordinate a row carries, with where it came from (B2, 2026-11-02). */
export interface StoredLocation {
  lat: number;
  lon: number;
  /** Who placed it: the row's own per-field tag for lat/lon (the MLS number, or "U.S. Census
   *  geocoder (<matched address>)" for an uploaded row the server located), else the server
   *  resolver's source, else the row's source. Never blank: "Source not stated" when none is. */
  source: string;
}

/**
 * THE ONE RULE FOR WHERE A SALE IS (B2, 2026-11-02). The comps map, the review map and the competing
 * listings all read a sale's location HERE, so a pin cannot sit in one place on step 3 and another on
 * step 4. Order: the source row's own latitude/longitude (an MLS or public-record row), which also
 * covers an uploaded row the server already located through its house-number/ZIP gate
 * (`subject_location.locate_comps` writes lat/lon and `_location`). (0, 0) is a missing value, not
 * a place. Null when the row carries no usable coordinate — the caller then geocodes the full
 * address behind the gate, or, when the server already tried and refused (`_location.status ===
 * "not_mapped"`), lists it as unplaced.
 */
export function storedLocation(c: Pick<CandidateComp, "lat" | "lon" | "_location" | "_field_source_labels" | "_source">): StoredLocation | null {
  // The row's coordinate as the wire may carry it: `lat`/`lon` (the MLS adapter's own keys), a numeric
  // string ("32.91", which the server's `valid_point` also accepts), or the long spellings a file or
  // another source can use. A blank or non-numeric value is missing, never zero.
  const row = c as unknown as Record<string, unknown>;
  const lat = coord(row.lat ?? row.latitude);
  const lon = coord(row.lon ?? row.lng ?? row.longitude);
  if (lat === null || lon === null || (lat === 0 && lon === 0)) return null;
  // A latitude/longitude outside the globe is a corrupt value, not a place.
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  const mapped = c._location?.status === "mapped" ? c._location : null;
  const tag = mapped?.pin_source
    ?? c._field_source_labels?.lat
    ?? mapped?.source
    ?? c._source;
  return { lat, lon, source: (tag ?? "").trim() || "Source not stated" };
}

/**
 * THE BROWSER GEOCODER'S GATE (B2, 2026-11-02), applied by BOTH maps — the client twin of the server resolver's
 * house-number/ZIP check (`subject_location.resolve_address`). A match is placed only when its own
 * street number equals the address's leading house number and, where the address states a ZIP,
 * its postal code equals it. Measured history (`addressPin.ts`): a bare street line resolved to a
 * different state four times in eight, and a match on the right street with the wrong number is
 * the same failure at a smaller scale.
 */
export function geocodeMatchPasses(
  address: string,
  result: { address_components?: { long_name?: string; short_name?: string; types?: string[] }[] },
): boolean {
  const comps = result?.address_components ?? [];
  const part = (t: string) => comps.find((c) => (c.types ?? []).includes(t));
  const wantNum = /^\s*(\d+)/.exec(address)?.[1] ?? null;
  const zipMatch = /\b(\d{5})(?:-\d{4})?\b/.exec(address.replace(/^\s*\d+(?:-\d+)?[A-Za-z]?\b/, ""));
  const wantZip = zipMatch?.[1] ?? null;
  if (wantNum) {
    const got = part("street_number")?.long_name ?? part("street_number")?.short_name;
    if (!got || got.replace(/\D.*$/, "") !== wantNum) return false;
  }
  if (wantZip) {
    const got = part("postal_code")?.long_name ?? part("postal_code")?.short_name;
    if (!got || got.slice(0, 5) !== wantZip) return false;
  }
  return true;
}

/** What a map geocodes for a sale with no stored location: the street line with the row's own city
 * and state — the SAME string on the comps map and the review map (B2: the review map used to send
 * the bare street line, which the address gate refused, so the sale was "Not mapped" on step 4
 * while step 3 had pinned it). */
export function compMapQuery(c: Pick<CandidateComp, "name" | "city" | "state"> & { zip?: string | null }): string {
  // An MLS row states a city and ZIP but no state; the ZIP is what makes the street line one place.
  const stateZip = [c.state, c.zip ? String(c.zip).trim().slice(0, 5) : ""].filter(Boolean).join(" ");
  return [c.name, c.city, stateZip].filter(Boolean).join(", ");
}

/** True when the server asked its geocoders for this row and none passed the gate. */
export function serverRefusedLocation(c: Pick<CandidateComp, "_location">): boolean {
  return c._location?.status === "not_mapped";
}

export interface UnplacedRow { number: number; name: string; caption: string; message: string; reason?: string }

/** `order` is the display order of indices into `comps`. Returns one point per comp (and, when
 * given, per competing listing) that can be placed, and the rows that cannot. A row with no
 * location is LISTED with `NOT_MAPPED_MESSAGE`; it is never given a guessed point, never (0, 0). */
export function buildMapPoints(
  comps: CandidateComp[],
  order: number[],
  actives: CandidateComp[] = [],
): { points: MapPoint[]; unplaced: UnplacedRow[] } {
  const points: MapPoint[] = [];
  const unplaced: UnplacedRow[] = [];
  const place = (c: CandidateComp, index: number, kind: MapPoint["kind"], number: number,
                 glyph: string, caption: string) => {
    const name = (c.name ?? "").trim() || "This sale";
    const at = storedLocation(c);
    const query = compMapQuery(c);
    const base = { index, kind, number, glyph, caption, name, selected: !!c._selected };
    if (at) {
      points.push({ ...base, lat: at.lat, lon: at.lon, query: null, source: at.source });
    } else if (serverRefusedLocation(c)) {
      // The server asked its geocoders under the house-number/ZIP gate and none passed. Do not
      // let the browser geocoder guess a pin the server refused.
      unplaced.push({ number, name, caption, message: NOT_MAPPED_MESSAGE, reason: c._location?.reason });
    } else if (isPinnable(query)) {
      points.push({ ...base, lat: null, lon: null, query, source: null });
    } else {
      unplaced.push({ number, name, caption, message: NOT_MAPPED_MESSAGE });
    }
  };
  order.forEach((index, pos) => {
    const c = comps[index];
    if (c) place(c, index, "comp", pos + 1, String(pos + 1), `Comp ${pos + 1}`);
  });
  actives.forEach((c, i) => {
    const word = listingStatusWord(c.status);
    place(c, ACTIVE_INDEX_BASE + i, "active", 0, word === "Pending" ? "P" : "A", word);
  });
  return { points, unplaced };
}

/** The next/previous point's comp index when stepping with the keyboard from the map region. */
export function stepActive(points: MapPoint[], current: number | null, dir: 1 | -1): number | null {
  if (!points.length) return null;
  const at = points.findIndex((p) => p.index === current);
  const next = at === -1 ? (dir === 1 ? 0 : points.length - 1) : (at + dir + points.length) % points.length;
  return points[next].index;
}

/** Pin look, as data: a selected comp is a filled navy disc; a candidate is a white disc with a navy
 * ring; the active one (hovered or focused row) is larger. Colour never carries it alone — the
 * number is on every pin and the rows say "Selected". */
export function pinStyle(p: Pick<MapPoint, "selected"> & { kind?: MapPoint["kind"] }, active: boolean) {
  // A competing listing (Active or Pending) is amber, so it is never mistaken for a sale; the
  // glyph (A / P) and the key carry it in words as well.
  if (p.kind === "active") {
    return {
      scale: active ? 15 : 12,
      fillColor: colors.amberBg,
      fillOpacity: 1,
      strokeColor: colors.amber,
      strokeWeight: active ? 3 : 2,
      labelColor: colors.amber,
    };
  }
  return {
    scale: active ? 15 : 12,
    fillColor: p.selected ? colors.accent : colors.bgSurface,
    fillOpacity: 1,
    strokeColor: colors.accent,
    strokeWeight: active ? 3 : 2,
    labelColor: p.selected ? colors.bgSurface : colors.accent,
  };
}

/** Miles to metres, for the radius circle. */
export const METERS_PER_MILE = 1609.344;

// -------------------------------------------------------------------------------------------
// THE MAP'S SHAPE AND FRAMING (2026-10-22, owner report on prod: "a very wide, short strip zoomed
// to street level around the single pin"). Measured in Chromium on main 446b364: the canvas was
// 1344 x 360 at 1440 (3.7 : 1), and the map was framed once, on the circle alone, so comps outside
// the circle were off the map at 390 (4 of 7 pins visible) and a "Search again" never re-framed.
// -------------------------------------------------------------------------------------------

export interface LatLng { lat: number; lng: number }
/** Google's `LatLngBoundsLiteral`, which `fitBounds` takes as is. */
export interface BoundsLiteral { north: number; south: number; east: number; west: number }

/** The closest the map frames itself: about a neighborhood. The agent can still zoom in by hand. */
export const MAP_MAX_ZOOM = 16;
/** Pixels kept clear inside each edge so a pin at the frame's edge is not cut in half. */
export const MAP_FIT_PADDING_PX = 32;
/** The smallest area the map ever frames: the standard search circle. With no comps this is the
 * frame, so the map never opens at street level around one pin. */
export const MIN_FRAME_RADIUS_MI = 0.5;
/** The square's largest side on a wide screen. */
export const MAP_MAX_SIDE_PX = 560;
/** At or below this viewport width the square takes the full column width. */
export const MAP_FULL_WIDTH_MAX_VIEWPORT = 768;

// Google's spherical radius (WGS84 semi-major axis), so these bounds match `Circle.getBounds()`.
const EARTH_RADIUS_M = 6378137;

/** The bounds of a circle of `radiusMi` around `center`. */
export function circleBounds(center: LatLng, radiusMi: number): BoundsLiteral {
  const dLat = ((radiusMi * METERS_PER_MILE) / EARTH_RADIUS_M) * (180 / Math.PI);
  const dLng = dLat / Math.cos((center.lat * Math.PI) / 180);
  return { north: center.lat + dLat, south: center.lat - dLat, east: center.lng + dLng, west: center.lng - dLng };
}

function union(a: BoundsLiteral | null, b: BoundsLiteral): BoundsLiteral {
  if (!a) return b;
  return { north: Math.max(a.north, b.north), south: Math.min(a.south, b.south),
           east: Math.max(a.east, b.east), west: Math.min(a.west, b.west) };
}

/** What the map frames: the subject's search circle, every comp pin that has a position, and never
 * less than a `MIN_FRAME_RADIUS_MI` circle around the middle of all of it. Null when there is
 * nothing to frame yet (no subject position and no pin). Pure, so the framing is tested without a
 * map. */
export function frameBounds({ subject, radiusMi, pins }: {
  subject: LatLng | null; radiusMi: number; pins: LatLng[];
}): BoundsLiteral | null {
  let b: BoundsLiteral | null = null;
  if (subject) b = circleBounds(subject, finite(radiusMi) && radiusMi > 0 ? radiusMi : MIN_FRAME_RADIUS_MI);
  for (const p of pins) {
    if (!finite(p.lat) || !finite(p.lng)) continue;
    b = union(b, { north: p.lat, south: p.lat, east: p.lng, west: p.lng });
  }
  if (!b) return null;
  const mid = { lat: (b.north + b.south) / 2, lng: (b.east + b.west) / 2 };
  return union(b, circleBounds(mid, MIN_FRAME_RADIUS_MI));
}

/** The square's CSS for a viewport width, written by the component through `setProperty` (I-8).
 * Wide screens cap the side at `MAP_MAX_SIDE_PX`; at or below `MAP_FULL_WIDTH_MAX_VIEWPORT` it is
 * the full column. The two-column placement beside the search panel is the stylesheet's
 * (`.vb-cs-body`, at 1024 px and up). */
export function mapCanvasStyle(viewportWidth: number): Record<string, string> {
  return {
    width: "100%",
    height: "auto",
    "aspect-ratio": "1 / 1",
    "max-width": viewportWidth <= MAP_FULL_WIDTH_MAX_VIEWPORT ? "none" : `${MAP_MAX_SIDE_PX}px`,
  };
}
